import { createSelector } from '@reduxjs/toolkit';
import {
  Map as ImmutableMap,
  Record as ImmutableRecord,
  Set as ImmutableSet,
  is,
  List,
  OrderedMap,
  type RecordOf,
} from 'immutable';
import { createCachedSelector, LruMapCache, LruObjectCache } from 're-reselect';

import { immutableEntriesShallowEqual } from 'app-components/utils/core/reselect';
import { registerCachedSelectors } from 'app-components/utils/core/reselect/selectorCacheRegistry';
import type RootState from 'app-components/utils/redux-store/rootState';

import store from '../../utils/redux-store';
import { ShowBalance } from '../calculated-balances/balanceTypes';
import { getBalanceForAccount } from '../calculated-balances/balanceUtils';
import { getId } from '../core/resourceStoreTypes';
import { type FEATURE_FLAGS_REDUCER_KEY, featureFlagsSelectors } from '../feature-flags';
import { INSTITUTION_LOGINS_REDUCER_KEY } from '../institution-logins/institutionLoginsTypes';
import { preferencesV2Selectors } from '../preferences-v2';
import {
  ACCOUNTS_REDUCER_KEY,
  type Account,
  type AccountProps,
  type AccountSubType,
  type AccountType,
  type NonGoalAccount,
  type VehicleAccountProps,
} from './accountsTypes';
import * as utils from './accountsUtils';
import { hasCheckNumberField } from './accountsUtils';

type AccountSubTypeOrder = Partial<Record<`${AccountType}:${AccountSubType | ''}`, number>>;

const spendingAccountsOrder: Record<string, number> = {
  'CREDIT:CREDIT_CARD': 0,
  'BANK:CHECKING': 1,
  'CASH:': 2,
  'CREDIT:LINE_OF_CREDIT': 3,
  'CREDIT:OTHER_CREDIT': 4,
  'BANK:OTHER_BANK': 5,
} satisfies AccountSubTypeOrder;

const investmentsOrder: Record<string, number> = {
  INVESTMENT: 0,
};

const accountGroupNameMap: Partial<Record<AccountSubType | AccountType | `Q${AccountSubType}`, string>> = {
  BROKERAGE: 'Brokerage',
  OTHER_INVESTMENTS: 'Other',
  IRA: 'Retirement',
  ROTH_401K: 'Retirement',
  ROTH_IRA: 'Retirement',
  SIMPLE_IRA: 'Retirement',
  UGMA_UTMA: 'Other',
  529: 'Other',
  Q401K: 'Retirement',
  Q529: 'Other',
  KEOGH: 'Retirement',
  Q403B: 'Other',
  SEP_IRA: 'Retirement',
  '401K': 'Retirement',
  '403B': 'Retirement',
};

/*
 * For these selectors, state is global state as it is provided to React (not specific to accounts)
 * We use them to select into the store and get data for this REDUCER_KEY
 */
export const getAccountStore = (state: Pick<RootState, ACCOUNTS_REDUCER_KEY>) => state[ACCOUNTS_REDUCER_KEY];

export const getLoadPending = createSelector([getAccountStore], (accountStore) => accountStore.loadPending);
export const getIsLoading = createSelector([getAccountStore], (accountStore) => accountStore.isLoading);
export const getError = createSelector([getAccountStore], (accountStore) => accountStore.error);
export const getResourcesById = createSelector([getAccountStore], (accountStore) => accountStore.resourcesById);
export const getAccountsByIdIncludingGoals = getResourcesById;

export type TransactionAccountClassification = Pick<AccountProps, 'type' | 'institutionLoginId'>;
const transactionAccountClassification = ImmutableRecord<TransactionAccountClassification>({
  type: 'UNKNOWN',
  institutionLoginId: undefined,
});

/** Transaction visibility depends on classification, not changing balances or account labels. Includes goals. */
export const getTransactionAccountClassificationById = createSelector(
  getAccountsByIdIncludingGoals,
  (accounts): ImmutableMap<string, RecordOf<TransactionAccountClassification>> =>
    accounts.map((account) =>
      transactionAccountClassification({
        type: account.type,
        institutionLoginId: account.institutionLoginId,
      }),
    ),
  { memoizeOptions: { resultEqualityCheck: is } },
);

export const getLastSyncDate = createSelector([getAccountStore], (accountStore) => accountStore.lastSyncDate);
export const getAccountById = (state: Pick<RootState, ACCOUNTS_REDUCER_KEY> = store!.getState(), id?: string | null) =>
  id ? getAccountStore(state).resourcesById.get(id) : undefined;

export const isNonGoalAccount = (acct: Account): acct is NonGoalAccount => acct.type !== 'GOAL';

// updated to not include Goal accounts
export const getAccountsById = createSelector([getResourcesById], (accountsById) =>
  accountsById.filter(isNonGoalAccount),
);

/** Names from getAccountsById; keeps its identity across balance and other non-name updates. */
export const getAccountNamesById = createSelector(
  [getAccountsById],
  (accountsById): ImmutableMap<string, string | undefined> => accountsById.map((account) => account.name),
  { memoizeOptions: { resultEqualityCheck: is } },
);

/** Same entry as getAccountsById(state).get(id); its identity changes only when that account changes. */
export const getNonGoalAccountById = (
  state: Pick<RootState, ACCOUNTS_REDUCER_KEY>,
  id?: string | null,
): NonGoalAccount | undefined => {
  const account = getAccountById(state, id);
  return account && isNonGoalAccount(account) ? account : undefined;
};

export const getOpenAccountsById = createSelector(getAccountsById, (accountsById) =>
  accountsById.filter((acct) => !acct.isClosed),
);

export const getOpenAccountIds = createSelector(getOpenAccountsById, (openAccounts) =>
  ImmutableSet(openAccounts.keys()),
);

export const getAccountIdsForInvestments = createSelector([getAccountsById], (accountsById) => {
  const accounts = accountsById.filter((acct) => acct.type === 'INVESTMENT');
  return {
    groupedAccounts: accounts
      .toList()
      .groupBy((x) => (x.subType ? accountGroupNameMap[x.subType] || 'Other' : accountGroupNameMap[x.type] || 'Other'))
      .sortBy((item, key) => investmentsOrder[key] || 100),
    accountIds: List([...accounts.keys()]),
  };
});

export const isAssetLiabilityAccount = (account: AccountProps) =>
  account?.type === 'LOAN' ||
  account?.type === 'OTHER_ASSET' ||
  account?.type === 'OTHER_LIABILITY' ||
  account?.type === 'REAL_ESTATE' ||
  account?.type === 'VEHICLE';

export const isVehicleAccount = (account: AccountProps): account is VehicleAccountProps => account?.type === 'VEHICLE';

export const isCashAccount = (account?: AccountProps) =>
  account?.type === 'CREDIT' || account?.type === 'BANK' || account?.type === 'CASH';

export const isGoalAccount = (account?: AccountProps) => account?.type === 'GOAL';

export const isBankAccount = (account?: AccountProps) =>
  (account?.type === 'BANK' || account?.subType === 'SAVINGS') && !account?.isClosed;

export const isInvestmentAccount = (account?: AccountProps) => account?.type === 'INVESTMENT';

export const isLoanAccount = (account?: AccountProps) => account?.type === 'LOAN';

export const isConnectedLoanAccount = (account?: AccountProps) =>
  account?.type === 'LOAN' && utils.isAccountConnected(account);

export const getLoanAccountIds = createSelector(
  getAccountsById,
  (accountsById): ImmutableSet<string> => {
    const accounts = accountsById.filter((acct) => isLoanAccount(acct));
    return ImmutableSet([...accounts.keys()]);
  },
  { memoizeOptions: { resultEqualityCheck: is } },
);

export const getConnectedLoanAccountIds = createSelector(
  getAccountsById,
  (accountsById): ImmutableSet<string> => ImmutableSet(accountsById.filter(isConnectedLoanAccount).keys()),
  { memoizeOptions: { resultEqualityCheck: is } },
);

export const getReportableAccountsById = createSelector(getAccountsById, (accountsById) =>
  accountsById.filter((account) => !account.isExcludedFromReports),
);

export const getHasReportableAccounts = createSelector(
  getReportableAccountsById,
  (accountsById) => accountsById.size > 0,
);

export const getHasAnyAccounts = createSelector(getAccountsById, (accts) => !accts.isEmpty());

export const getVisibleAccountsById = createSelector(getAccountsById, (accountsById) =>
  accountsById.filter((x) => !x.isExcludedFromAccountBar),
);

const emptyAccountMap = ImmutableMap<string, Account>();
export const getCashAccounts = createSelector(getAccountsById, (accountsById) =>
  utils.filterAccountsByPredicate(accountsById, isCashAccount, emptyAccountMap),
);

export const getBankAccounts = createSelector(getAccountsById, (accountsById) =>
  utils.filterAccountsByPredicate(accountsById, isBankAccount, emptyAccountMap),
);

export const getRealEstateAccounts = createSelector(getAccountsById, (accountsById) =>
  utils.filterAccountsByPredicate(accountsById, (account) => account.type === 'REAL_ESTATE', emptyAccountMap),
);

export const getVehicleAccounts = createSelector(getAccountsById, (accountsById) =>
  utils.filterAccountsByPredicate(accountsById, (account) => account.type === 'VEHICLE', emptyAccountMap),
);

export const getManualVehicleAccounts = createSelector(getVehicleAccounts, (vehicleAccountsById) =>
  vehicleAccountsById == null
    ? emptyAccountMap
    : vehicleAccountsById.filter((vehicleAccount) => vehicleAccount.institutionLoginId === undefined),
);

export const getCashFlowTransactionOnlyAccounts = createSelector(getAccountsById, (accountsById) =>
  accountsById?.filter((account) => isCashAccount(account) || isAssetLiabilityAccount(account)),
);

export const getCashFlowTransactionOnlyAccountIds = createSelector(
  getCashFlowTransactionOnlyAccounts,
  (accountsById): string[] => accountsById?.keySeq().toArray() ?? [],
);

export const getStripeAccounts = createSelector(getAccountsById, (accountsById) =>
  utils.filterAccountsByPredicate(accountsById, utils.isStripeAccount, emptyAccountMap),
);

export const isCreditCardAccount = (account?: AccountProps) =>
  account?.type === 'CREDIT' && account?.subType === 'CREDIT_CARD';

export const getCreditCardAccounts = createSelector(getAccountsById, (accountsById) =>
  utils.filterAccountsByPredicate(accountsById, isCreditCardAccount, emptyAccountMap),
);

// The credit card dashboard is personal-only: cards owned by a business are excluded everywhere in it.
export const getPersonalCreditCardAccounts = createSelector(getCreditCardAccounts, (creditCardAccountsById) =>
  utils.filterAccountsByPredicate(creditCardAccountsById, (account) => !account?.businessId, emptyAccountMap),
);

export const getHasCreditCardNeedingAttention = createSelector(
  getPersonalCreditCardAccounts,
  (creditCardAccountsById) =>
    Boolean(creditCardAccountsById?.some((account) => utils.isAccountNeedsAttention(account))),
);

export const getSortedCreditCardAccounts = createSelector(
  [
    getPersonalCreditCardAccounts,
    // Session-only: the caller (CardSummary) keeps sort in local component state rather than a
    // persisted preference, so it doesn't carry over between sessions. Callers that don't pass
    // one (e.g. mobile, which has no sort UI of its own) get alphabetical order.
    (_state: unknown, sortMode = 'cardName') => sortMode,
    (state) =>
      preferencesV2Selectors.getPreferenceValue(state, 'sAccountsBankBalance', ShowBalance.INCLUDE_PENDING) as string,
  ],
  (creditCardAccountsById, sortMode, balancePreference) => {
    if (creditCardAccountsById == null || creditCardAccountsById.isEmpty()) {
      return List<Account>();
    }

    const accounts = creditCardAccountsById.toList();

    const getBalance = (account: Account): number => {
      return Math.abs(getBalanceForAccount(account, balancePreference));
    };

    const getUtilization = (account: Account): number => {
      const { value: creditLimit } = utils.getResolvedAccountCreditLimit(account);
      // To get a proper utilization value, creditLimit MUST be a number AND creditLimit MUST be above 0.
      // Otherwise, it is invalid and it should be sorted LAST, so we return -Infinity.
      if (!(typeof creditLimit === 'number' && creditLimit > 0)) return -Infinity;
      const balance = getBalance(account);
      return (balance / creditLimit) * 100;
    };

    const byName = (a: Account, b: Account): number => (a.name ?? '').localeCompare(b.name ?? '');

    const compare = (a: Account, b: Account): number => {
      const aClosedPriority = a.isClosed ? 1 : 0;
      const bClosedPriority = b.isClosed ? 1 : 0;

      // closed accounts always sort to end
      if (aClosedPriority !== bClosedPriority) {
        return aClosedPriority - bClosedPriority;
      }

      switch (sortMode) {
        case 'attentionNeeded': {
          // Erroring cards first, then disconnected ones, then everything else.
          const attentionRank = (account: Account): number => {
            if (!utils.isAccountNeedsAttention(account)) return 2;
            return utils.isAccountDisconnected(account) ? 1 : 0;
          };
          return attentionRank(a) - attentionRank(b) || byName(a, b);
        }
        case 'largestBalance': {
          const aBalance = getBalance(a);
          const bBalance = getBalance(b);
          // Secondary tiebreaker: identical balances fall back to alphabetical-by-name so
          // ties don't depend on incidental input order (SIMPL-35102).
          return bBalance - aBalance || byName(a, b); // descending
        }
        case 'highestUtilization': {
          const aUtil = getUtilization(a);
          const bUtil = getUtilization(b);
          return bUtil - aUtil || byName(a, b); // descending
        }
        case 'highestApr': {
          // Missing/unset rates rank strictly below every real value (including a genuine 0%),
          // matching today's behavior of leaking the unset sentinel through as a very low number.
          const aApr = utils.getResolvedAccountInterestRate(a).value ?? -Infinity;
          const bApr = utils.getResolvedAccountInterestRate(b).value ?? -Infinity;
          return bApr - aApr || byName(a, b); // descending
        }
        case 'cardName':
        default:
          return byName(a, b);
      }
    };

    return accounts.sort(compare);
  },
);

export type CreditCardUtilizationSummary = {
  totalUsed: number;
  totalBalance: number;
  totalLimit: number;
  remainingCredit: number;
  percentUsed: number;
  isIncomplete: boolean;
  hasCreditLimits: boolean;
  cardCount: number;
  notClosedCardCount: number;
};

export const getCreditCardUtilizationSummary = createSelector(
  [
    getPersonalCreditCardAccounts,
    (state) =>
      preferencesV2Selectors.getPreferenceValue(state, 'sAccountsBankBalance', ShowBalance.INCLUDE_PENDING) as string,
  ],
  (creditCardAccountsById, balancePreference): CreditCardUtilizationSummary => {
    let totalUsed = 0;
    let totalLimit = 0;
    let isIncomplete = false;
    let hasCreditLimits = false;
    let cardCount = 0;
    let notClosedCardCount = 0;
    let totalBalance = 0;

    creditCardAccountsById.forEach((account) => {
      cardCount += 1;
      if (account.isClosed) {
        return;
      }
      notClosedCardCount += 1;
      const balance = getBalanceForAccount(account, balancePreference);
      totalBalance += balance;

      const { value: creditLimit } = utils.getResolvedAccountCreditLimit(account);
      if (typeof creditLimit === 'number') {
        hasCreditLimits = true;
      }
      // Erroring or disconnected cards can't have their limit trusted, so they stay out of the totals.
      if (utils.isAccountNeedsAttention(account)) {
        isIncomplete = true;
        return;
      }
      if (typeof creditLimit !== 'number') {
        isIncomplete = true;
        return;
      }
      // A $0 limit contributes nothing to totalLimit, so its balance must also be excluded from
      // totalUsed — otherwise percentUsed would count debt that isn't backed by any limit.
      if (creditLimit !== 0) {
        totalUsed -= balance; // Negating totalUsed only
        totalLimit += creditLimit;
      }
    });
    const percentUsed = totalLimit > 0 ? (totalUsed / totalLimit) * 100 : 0;

    return {
      totalUsed,
      totalBalance,
      totalLimit,
      remainingCredit: totalLimit - totalUsed,
      percentUsed,
      isIncomplete,
      hasCreditLimits,
      cardCount,
      notClosedCardCount,
    };
  },
);

export const getOpenOrSelectedCashAccounts = createSelector(
  [getCashFlowTransactionOnlyAccounts, (_state: unknown, selectedAccountId?: string) => selectedAccountId],
  (accountsById, selectedAccountId) =>
    accountsById == null
      ? emptyAccountMap
      : accountsById.filter((account) => !account?.isClosed || selectedAccountId === account?.id),
);

// Entry equality keeps the map across refreshes that leave the investment accounts unchanged.
export const getInvestmentAccounts = createSelector(
  getAccountsById,
  (accountsById) => (accountsById == null ? emptyAccountMap : accountsById.filter((x) => isInvestmentAccount(x))),
  { memoizeOptions: { resultEqualityCheck: immutableEntriesShallowEqual } },
);

export const getOpenOrSelectedInvestmentAccounts = createSelector(
  [getInvestmentAccounts, (_state: unknown, selectedAccountId: string) => selectedAccountId],
  (accountsById, selectedAccountId) =>
    accountsById == null
      ? emptyAccountMap
      : accountsById.filter((account) => !account?.isClosed || selectedAccountId === account?.id),
);

export const getNonConnectedInvestmentAccounts = createSelector(getInvestmentAccounts, (accountsById) =>
  accountsById?.filter((account) => utils.isAccountManual(account)),
);

export const getOpenNonConnectedInvestmentAccounts = createSelector(
  getNonConnectedInvestmentAccounts,
  (accountsById) => accountsById?.filter((account) => !account?.isClosed),
  { memoizeOptions: { resultEqualityCheck: immutableEntriesShallowEqual } },
);

export const getOpenCashOrInvestmentAccounts = createSelector(
  [getCashAccounts, getInvestmentAccounts],
  (cashAccountsById, investmentAccountsById) => {
    const cash = cashAccountsById ?? emptyAccountMap;
    const investments = investmentAccountsById ?? emptyAccountMap;
    return cash
      .merge(investments)
      .filter((account) => !account.isClosed)
      .filter(utils.isAccountConnected);
  },
);

//-------------------------------------------------------------------------------------------------
// Selector that returns a the set of all account IDs. The returned set will only change when an
// account is added/deleted.
//-------------------------------------------------------------------------------------------------
export const getAllAccountIds = createSelector(
  getAccountsByIdIncludingGoals,
  (accountsById): ImmutableSet<string> => ImmutableSet(accountsById.keys()),
  { memoizeOptions: { resultEqualityCheck: is } },
);

//-------------------------------------------------------------------------------------------------
// Selector that returns a map of account types keyed by the account ID. The returned value will
// only change when accounts are added/deleted or when an account type has changed.
//-------------------------------------------------------------------------------------------------
export const getAccountTypesById = createSelector(
  getAccountsByIdIncludingGoals,
  (accountsById): ImmutableMap<string, AccountType> => accountsById.map((account) => account.type),
  { memoizeOptions: { resultEqualityCheck: is } },
);

//-------------------------------------------------------------------------------------------------
// Selector that returns a the set of goal account IDs. This can be used to quickly determine whether
// an account is a GOAL account or not. The returned set will only change when a goal account is
// added/deleted and an existing account has changed to/from a GOAL account.
//-------------------------------------------------------------------------------------------------
export const getGoalAccountIds = createSelector(
  getAccountTypesById,
  (accountTypesById): ImmutableSet<string> => ImmutableSet(accountTypesById.filter((type) => type === 'GOAL').keys()),
  { memoizeOptions: { resultEqualityCheck: is } },
);

export const getInvestmentAccountIds = createSelector(
  getAccountTypesById,
  (accountTypesById): ImmutableSet<string> =>
    ImmutableSet(accountTypesById.filter((type) => type === 'INVESTMENT').keys()),
  { memoizeOptions: { resultEqualityCheck: is } },
);

export const getAccountsByInstitutionLoginId = createSelector(getAccountsById, (accountsById) =>
  accountsById?.reduce((accountsByInstitutionLoginIds, account) => {
    const institutionLoginId = account.institutionLoginId || '0';
    const accounts = accountsByInstitutionLoginIds.get(institutionLoginId) || List<Account>();
    return accountsByInstitutionLoginIds.set(institutionLoginId, accounts.push(account));
  }, ImmutableMap<string, List<Account>>()),
);

export const getAccountsForInstitutionLoginId = createSelector(
  [
    getAccountsByInstitutionLoginId,
    (_state: unknown, props: { institutionLoginId: string }) => props.institutionLoginId,
  ],
  (accountsByInstitutionId, institutionLoginId) => accountsByInstitutionId?.get(institutionLoginId) || List<Account>(),
);

export const getAccountIdsForInstitutionLoginId = createSelector(getAccountsForInstitutionLoginId, (accounts) =>
  accounts.map(getId),
);

export const getSpendingAccounts = createSelector(getVisibleAccountsById, (accounts) =>
  OrderedMap(
    accounts.filter((account) =>
      Object.prototype.hasOwnProperty.call(spendingAccountsOrder, `${account.type}:${account.subType}`),
    ),
  ).sort(
    (account1, account2) =>
      spendingAccountsOrder[`${account1.type}:${account1.subType}`] -
      spendingAccountsOrder[`${account2.type}:${account2.subType}`],
  ),
);

const savingAccountsOrder: Record<string, number> = {
  'BANK:SAVINGS': 0,
  'BANK:CD': 1,
  'BANK:MONEY_MARKET': 2,
} satisfies AccountSubTypeOrder;
export const getSavingAccounts = createSelector(getVisibleAccountsById, (accounts) =>
  OrderedMap(
    accounts.filter((account) =>
      Object.prototype.hasOwnProperty.call(savingAccountsOrder, `${account.type}:${account.subType}`),
    ),
  ).sort(
    (account1, account2) =>
      savingAccountsOrder[`${account1.type}:${account1.subType}`] -
      savingAccountsOrder[`${account2.type}:${account2.subType}`],
  ),
);

export const accountIds = createCachedSelector(
  (state: Pick<RootState, ACCOUNTS_REDUCER_KEY>, accounts: ImmutableMap<string, Account> | undefined) =>
    accounts || getAccountsById(state),
  (accounts) => accounts.filter((account) => account.id).map((account) => account.id),
)({
  keySelector: (state, accounts) => accounts?.hashCode() ?? 'no-accounts',
  cacheObject: new LruObjectCache({ cacheSize: 8 }),
});

export const getTransactionAccounts = createSelector(
  [
    getAccountsById, // goals are excluded by default
    (state: Pick<RootState, typeof FEATURE_FLAGS_REDUCER_KEY>) =>
      featureFlagsSelectors.getFeatureFlag(state, 'investmentTransactions'),
  ],
  (accountsById, investmentTransactions) =>
    accountsById.filter(
      (account) =>
        (account?.type !== 'INVESTMENT' || investmentTransactions) &&
        (account?.type !== 'LOAN' || !utils.isAccountConnected(account)),
    ),
);

export const getAccountsByIds = createCachedSelector(
  [getAccountsById, (state: unknown, ids: List<string> | undefined) => ids],
  (accountsById, ids) => accountsById && accountsById.filter((account) => account.id && ids?.includes(account.id)),
)({
  keySelector: (state, ids) => ids?.hashCode() ?? 'no-accounts',
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export const getOpenAccountsByIds = createCachedSelector(
  [getOpenAccountsById, (state: unknown, ids: List<string> | undefined) => ids],
  (accountsById, ids) => accountsById && accountsById.filter((account) => account.id && ids?.includes(account.id)),
)({
  keySelector: (state, ids) => ids?.hashCode() ?? 'no-accounts',
  cacheObject: new LruMapCache({ cacheSize: 10 }),
});

export const getRefundAccounts = createSelector(getCashAccounts, (accountsById) =>
  accountsById?.filter((account) => !account.isClosed),
);

export const removeClosedAccountsFromIds = createSelector(
  [getOpenAccountIds, (state: unknown, providedAccountIds: List<string>) => providedAccountIds],
  (openAccountIds, providedAccountIds) => providedAccountIds?.filter?.((id) => openAccountIds.has(id)),
);

//-------------------------------------------------------------------------------------------------
// Business Related Selectors
//-------------------------------------------------------------------------------------------------

// Value equality keeps both lists stable across account refreshes that leave the A/R accounts unchanged.
export const getAllArAccounts = createSelector(
  getAccountsById,
  (accountsById) => accountsById.filter((account: AccountProps) => account.type === 'ACCOUNTS_RECEIVABLE').toList(),
  { memoizeOptions: { resultEqualityCheck: is } },
);

export const getAllArAccountIds = createSelector(
  getAllArAccounts,
  (accounts) => accounts.map((account) => account.id),
  {
    memoizeOptions: { resultEqualityCheck: is },
  },
);

export const getArAccountByBusiness = createSelector(getAccountsById, (accountsById) =>
  accountsById
    .filter((account: AccountProps) => account.type === 'ACCOUNTS_RECEIVABLE')
    .groupBy((account) => account.businessId)
    .map((accounts) => accounts.first()),
);

export const getArAccountForBusinessId = createSelector(
  getArAccountByBusiness,
  (_state, businessId: string) => businessId,
  (accountsByBusiness, businessId) => accountsByBusiness.get(businessId),
);

export const getPaymentSrcsByAccountId = createSelector(getOpenAccountsById, (accountsById) =>
  OrderedMap(accountsById.filter((account) => utils.doesAccountSupportPaymentSrcs(account))).sort((a, b) =>
    (a.name || '').localeCompare(b.name || ''),
  ),
);

// Refund compatible accounts are generally asset accounts; however, some types or subtypes
// are not applicable.
//
export const getRefundSrcsByAccountId = createSelector([getOpenAccountsById], (accountsById) =>
  OrderedMap(
    accountsById
      .filter((account) => utils.doesAccountSupportRefundSrcs(account))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
  ),
);

const getPriorityForDefaultPaymentAccount = (account: AccountProps) => {
  if (account.type === 'OTHER_ASSET' && account.subType === 'UNDEPOSITED_FUNDS') {
    return 6;
  }
  if (account.usageType === 'BUSINESS' && account.type !== 'PAYMENT') {
    return 5;
  }
  if (account.type === 'BANK') {
    if (account.subType === 'CHECKING') {
      return 4;
    }
    return 3;
  }
  if (account.type === 'CASH') {
    return 2;
  }
  if (account.type === 'OTHER_ASSET') return 1;
  return 0;
};

// This should return the undeposited funds account. If more than one, then return first one found.
// If there is no undeposited funds account, will try to pick best one based on usage type, type
// and subtype.
//
// Take business into account when selecting the preferred account. The business could be
//       passed into the selector as a parameter and if not passed in, we could use the default
//       business.
// FIXME: We can’t retrieve the default business due to a circular dependency—>businessSelectors rely on accountSelectors.
export const getDefaultPaymentSrcAccount = createSelector(
  [getPaymentSrcsByAccountId, (_state: unknown, businessId?: string) => businessId],
  (accountsById, businessId) => {
    let defaultPaymentSrcAccount: undefined | Account;
    let defaultPaymentSrcAccountPriority = 0;
    const businessAccountsById = businessId
      ? accountsById.filter((account) => account.businessId === businessId)
      : accountsById;
    businessAccountsById.forEach((account) => {
      const priority = getPriorityForDefaultPaymentAccount(account);
      if (priority > defaultPaymentSrcAccountPriority) {
        defaultPaymentSrcAccount = account;
        defaultPaymentSrcAccountPriority = priority;
      }
    });
    return defaultPaymentSrcAccount;
  },
);

export const getDefaultRefundSrcAccount = createSelector(
  [getAccountsById, (_state: unknown, businessId: string) => businessId],
  (accountsById, businessId) => {
    return accountsById?.find(
      (account) => account.businessId === businessId && account.type === 'BANK' && account.subType === 'CHECKING',
    );
  },
);

export const shouldAccountDisplayCheckNumberField = createSelector(
  [(s, id: string | null | undefined) => getAccountById(s, id)],
  (account) => {
    return hasCheckNumberField(account);
  },
);

export const stripeAccountForBizId = createSelector(
  [getResourcesById, (_s, businessId?: string | null) => businessId],
  (accounts, businessId) =>
    businessId
      ? accounts?.find((acc) => utils.isStripeAccount(acc) && acc.type === 'PAYMENT' && acc.businessId === businessId)
      : undefined,
);

export const stripeAccountPaymentMethods = createSelector(
  stripeAccountForBizId,
  (account) => account?.aggregators?.get(0)?.paymentMethods,
);

export const getSalesTaxAccountsByBusiness = createSelector(getAccountsById, (accountsById) =>
  accountsById
    .filter((account: AccountProps) => account.type === 'OTHER_LIABILITY' && account.subType === 'SALES_TAX')
    .groupBy((account) => account.businessId)
    .map((accounts) => accounts.first()),
);

// Value equality keeps the id list across refreshes that leave the QBP accounts' ids unchanged.
export const getQBPAccountIds = createSelector(
  getAccountsById,
  (accounts) => {
    return accounts
      ?.toList()
      ?.filter(utils.isQBPAccount)
      ?.map((account) => account.id);
  },
  { memoizeOptions: { resultEqualityCheck: is } },
);

export const isBalanceReCalcEligible = createSelector(
  getAccountById,
  (state, accountId) =>
    state[INSTITUTION_LOGINS_REDUCER_KEY]?.resourcesById?.get(
      getAccountById(state, accountId)?.institutionLoginId || '', // avoid circular dependency
    ),
  (account, institutionLogin) =>
    !account?.isClosed &&
    (!account?.isConnected ||
      institutionLogin?.channel === 'ZILLOW' ||
      institutionLogin?.channel === 'KELLEY_BLUE_BOOK'),
);

export const getAccountInterestRate = createCachedSelector(
  [(state: Pick<RootState, ACCOUNTS_REDUCER_KEY>, id: string | null | undefined) => getAccountById(state, id)],
  utils.getResolvedAccountInterestRate,
)((_state, id) => id ?? '');

export const getAccountCreditLimit = createCachedSelector(
  [(state: Pick<RootState, ACCOUNTS_REDUCER_KEY>, id: string | null | undefined) => getAccountById(state, id)],
  utils.getResolvedAccountCreditLimit,
)((_state, id) => id ?? '');

// A store reset drops every key's selector (see selectorCacheRegistry).
registerCachedSelectors(
  accountIds,
  getAccountsByIds,
  getOpenAccountsByIds,
  getAccountInterestRate,
  getAccountCreditLimit,
);
